import bridge from '@/plugins/vue-constructor-bridge';

import http from '@/services/http';
import events from '@/services/events';
import errorHandlers from '@/services/error-handlers';
import paymentGateway from '@/services/payment-gateway';
import PickupTimesService from '@/services/pickup-times';


import calculateDeviceTimezone from '@/helpers/calculate-device-timezone';
import parseLocation from '@/helpers/parse-location';



export default {
  namespaced: true,

  state: {
    searchCurrentPage: 1,
    searchFilters: {
      attributes: []
    },
    searchMoreResultsAreLoading: false,
    searchPageSize: 10,
    searchResults: [],
    searchResultsAreLoading: false,
    searchTotalPages: 1,
    searchQuery: null,
    selectedLocation: {
      address: {},
      allowAfterHoursOrdering: false,
      allowSameDayOutsideBusinessHoursOrdering: false,
      asapPickupTimestamp: null,
      attributes: [],
      businessLocationId: null,
      gtmLocationId: null,
      dailyOperatingSchedules: null,
      description: null,
      isLoading: false,
      leadPickupTime: null,
      maximumOrderAheadDays: 0,
      menuId: null,
      paymentGateway: {
        methods: {},
        providers: {}
      },
      paymentLocationId: null,
      phoneNumber: null,
      pos: {},
      storeId: null,
      timeZone: null,
      validations: [],
      tippingOptions: []
    }
  },

  getters: {
    searchResults(state, getters, rootState, rootGetters) {
      const locations = [];
      const orderDetails = rootGetters['session/orderDetails'];
      const moreResultsExist = state.searchCurrentPage < state.searchTotalPages;

      state.searchResults.forEach((result) => {
        const location = parseLocation({
          location: result,
          orderDetails
        });

        let supported = true;
        const messages = [];

        state.searchFilters.attributes.forEach((filter) => {
          const codes = filter.split(',');
          const attribute = location.attributes.find(a => codes.includes(a.code)); // // //
          // NOTE: This supports matching one of multiple codes. EG: "Curbside,TakeOut"  //

          if (!attribute) {
            supported = false;
          }

          else if (!attribute.value) {
            supported = false;
            messages.push(attribute.message);
          }
        });

        locations.push({
          ...location,
          searchedAttributes: {
            messages,
            supported
          }
        });
      });

      return {
        areLoading: state.searchResultsAreLoading,
        locations,
        moreResultsAreLoading: state.searchMoreResultsAreLoading,
        moreResultsExist
      };
    },

    selectedLocation(state, getters, rootState, rootGetters) {
      const orderDetails = rootGetters['session/orderDetails'];
      const { merchant } = rootState;

      return parseLocation({
        location: state.selectedLocation,
        merchant,
        orderDetails
      });
    },

    timeZone(state) {
      return state.selectedLocation.timeZone || calculateDeviceTimezone();
    }
  },

  actions: {
    async getDefaultLocation({ dispatch, rootGetters }) {
      try {
        const merchant = rootGetters['merchant/merchant'];

        if (!merchant.options.autoLoadStoreId) {
          return;
        }

        await dispatch('session/updateLocationId', merchant.options.autoLoadStoreId, { root: true });

        await dispatch('getLocation');

        await dispatch('orders/updateOrder', {
          fulfillmentType: 'pick-up',
          menuType: null
        }, { root: true });
      }

      catch (error) {
        errorHandlers.generic(error);
      }
    },

    async getLocation({ commit, dispatch, rootState }, { silent, withMenu } = {}) {
      const { locationId } = rootState.session;

      try {
        if (locationId) {
          if (!silent) {
            commit('SET_SELECTED_LOCATION_LOAD_STATUS', true);
          }

          const response = await http.get('/cardfree-api/v1/location/store', {
            params: {
              ids: locationId
            }
          });

          const [location] = response.data.data;

          if (location) {
            await dispatch('selectLocation', { location, silent, withMenu });
          }

          else {
            throw new Error('location not found');
          }
        }

        else {
          await dispatch('getDefaultLocation');
        }
      }

      catch (error) {
        dispatch('session/unsetLocation', null, { root: true });

        dispatch('session/unsetOrder', {}, { root: true });

        errorHandlers.generic(error);
      }

      finally {
        commit('SET_SELECTED_LOCATION_LOAD_STATUS', false);
      }
    },

    async getLocationConditionally({ commit, dispatch, rootGetters, state }, { withMenu = false } = {}) {
      /*
       * NOTE:
       * storeId = CardFree store ID
       * merchantLocationId = Merchant location ID driven by store POS
      */

      const session = rootGetters['session/session'];

      const { merchantLocationId, storeId } = session.route;

      const { selectedLocation } = state;

      if (selectedLocation.storeId && selectedLocation.menuId) {
        /*
         * NOTE: Even though the location is the same, the orderType might have changed,
         * which means we need to re-run some logic like loading the menu so we dispatch
         * 'selectLocation' again.
        */

        try {
          await dispatch('selectLocation', { location: selectedLocation, withMenu, silent: true });
        }

        catch (error) {
          errorHandlers.generic(error);
        }
      }
      else {
        commit('UNSET_SELECTED_LOCATION');

        if (merchantLocationId) {
          await dispatch('getLocationByMerchantId', { merchantLocationId, withMenu });
        }

        else {
          if (storeId) {
            dispatch('session/updateLocationId', storeId, { root: true });
          }

          await dispatch('getLocation', { withMenu });
        }
      }
    },

    async getLocationByMerchantId({ commit, dispatch }, { merchantLocationId, withMenu }) {
      try {
        commit('SET_SELECTED_LOCATION_LOAD_STATUS', true);

        const { data: location } = await http.get(`/cardfree-api/v1/location/merchant-location/${merchantLocationId}`);

        await dispatch('selectLocation', { location, withMenu });
      }

      catch (error) {
        errorHandlers.generic(error);
      }

      finally {
        commit('SET_SELECTED_LOCATION_LOAD_STATUS', false);
      }
    },

    async loadMenuForSelectedLocation({ commit, dispatch, state }, { silent = false } = {}) {
      try {
        if (state.selectedLocation.menuId) {
          if (!silent) commit('SET_SELECTED_LOCATION_LOAD_STATUS', true);

          await dispatch('menu/getMenu', { silent }, { root: true });
        }
      }

      catch (error) {
        errorHandlers.generic(error);
      }

      finally {
        commit('SET_SELECTED_LOCATION_LOAD_STATUS', false);
      }
    },

    async loadPaymentGatewayInfoForSelectedLocation({ commit }, location) {
      try {
        if (location?.storeId) {
          const gateway = await paymentGateway.load(location);
          commit('SET_PAYMENT_GATEWAY', gateway);
        }
      }

      catch (error) {
        throw error;
      }
    },

    async getPickupDaysforSelectedLocation({ getters, rootGetters }) {
      const { selectedLocation } = getters;

      const { menuType } = rootGetters['menu/menu'];

      const merchant = rootGetters['merchant/merchant'];

      const order = rootGetters['orders/order'];

      const { fulfillmentType, deliveryEstimate } = rootGetters['session/orderDetails'];

      const isDelivery = fulfillmentType === 'delivery';

      const getPickupDaysPayload = {
        location: selectedLocation,
        menuType,
        merchant,
        order
      };

      if (isDelivery) {
        Object.assign(getPickupDaysPayload, { deliveryTimeOffset: deliveryEstimate.deliveryTimeOffset });
      }

      try {
        const pickupDaysResult = await PickupTimesService.getPickupDays(getPickupDaysPayload);

        if (pickupDaysResult.length) {
          return pickupDaysResult;
        }

        const emitPayload = {
          customMessage: {
            title: 'Sorry, ordering is now closed.',
            description: 'Please try again during business hours. We apologize for the inconvenience!'
          }
        };

        events.emit('ordering-error', emitPayload);

        return [];
      }
      catch (error) {
        throw error;
      }
    },

    async moveOrderToSelectedLocation({ dispatch, rootGetters }, location) {
      try {
        const order = rootGetters['orders/order'];

        if (order.orderId && order.store.storeId !== location.storeId) {
          const { data: movedOrder } = await http.post(`/cardfree-api/v1/account/me/order/${order.orderId}/move`, {
            newStoreId: location.storeId
          });

          await dispatch('orders/updateOrderWithResponse', { order: movedOrder }, { root: true });
        }
      }

      catch (error) {
        throw error;
      }
    },

    resetSearchLocations({ commit, state }) {
      commit('SET_CURRENT_SEARCH_PAGE', 1);
      commit('SET_SEARCH_QUERY', null);

      if (state.selectedLocation.storeId) {
        commit('SET_SEARCH_RESULTS', {
          data: [state.selectedLocation]
        });
      }

      else {
        commit('SET_SEARCH_RESULTS', {
          data: []
        });
      }
    },

    async searchLocations({ commit, state }, options) {
      try {
        const query = {
          distance: options.distance,
          latitude: options.latitude,
          longitude: options.longitude
        };

        commit('SET_SEARCH_LOADING_STATUS', true);
        commit('SET_SEARCH_QUERY', query);
        commit('SET_CURRENT_SEARCH_PAGE', 1);

        const response = await http.get('/cardfree-api/v1/location/search', {
          params: {
            ...query,
            currentPage: 0,
            pageSize: state.searchPageSize
          }
        });

        commit('SET_SEARCH_RESULTS', response.data);
      }

      catch (error) {
        errorHandlers.generic(error);
      }

      finally {
        commit('SET_SEARCH_LOADING_STATUS', false);
      }
    },

    async selectLocation({ commit, dispatch, state, rootGetters }, { location, withMenu, silent = false, isOrderReset = false }) {
      try {
        const merchantLoyaltyOptions = rootGetters['merchant/loyaltyOptions'];

        const isNewSelectedLocation = state.selectedLocation.storeId !== location.storeId;

        if (isNewSelectedLocation) {
          await dispatch('loadPaymentGatewayInfoForSelectedLocation', location);

          if (!isOrderReset) await dispatch('moveOrderToSelectedLocation', location);

          if (merchantLoyaltyOptions.isStoreSpecificLoyalty) {
            await dispatch('offers/getLoyaltySummary', { storeId: location.storeId }, { root: true });
          }

          await dispatch('content/updateContent', location, { root: true });
        }

        http.defaults.headers.common['time-zone'] = location.timeZone;

        await dispatch('session/updateLocationId', location.storeId, { root: true });

        commit('SET_SELECTED_LOCATION', location);

        bridge.triggerNewRelicEvent('SelectLocation', 'Information', 'Select Location');

        /**
         * This must be called after the location is set, because downstream method
         * calls are dependent on there being a `selectedLocation` value.
         */
        if (withMenu) await dispatch('loadMenuForSelectedLocation', { silent });
      }

      catch (error) {
        throw error;
      }
    },

    async showMoreSearchLocations({ commit, state }) {
      try {
        const nextPage = state.searchCurrentPage + 1;

        commit('SET_SHOW_MORE_LOADING_STATUS', true);
        commit('SET_CURRENT_SEARCH_PAGE', nextPage);

        const response = await http.get('/cardfree-api/v1/location/search', {
          params: {
            ...state.searchQuery,
            pageSize: state.searchPageSize,
            currentPage: nextPage - 1
          }
        });

        response.data.data = [...state.searchResults, ...response.data.data];

        commit('SET_SEARCH_RESULTS', response.data);
      }

      catch (error) {
        errorHandlers.generic(error);
      }

      finally {
        commit('SET_SHOW_MORE_LOADING_STATUS', false);
      }
    },

    updateSearchFilters({ commit }, criterea) {
      const filters = {
        attributes: ['WebOrdering']
      };

      if (criterea.fulfillmentType === 'delivery') {
        filters.attributes.push('Delivery');
      }

      if (criterea.fulfillmentType === 'pick-up') {
        filters.attributes.push('Curbside,TakeOut');
      }

      if (criterea.fulfillmentType === 'room-service') {
        filters.attributes.push('RoomService');
      }

      if (criterea.orderType === 'group') {
        filters.attributes.push('GroupOrdering');
      }

      if (criterea.orderType === 'catering') {
        filters.attributes.push('CateringOrdering');
      }

      commit('SET_SEARCH_FILTERS', filters);
    },

    validateSelectedLocationWithOrderDetails({ dispatch, getters, rootGetters }) {
      const orderDetails = rootGetters['session/orderDetails'];
      const { selectedLocation } = getters;

      const attributeCodeMap = {
        delivery: {
          code: 'Delivery',
          display: 'Delivery'
        },
        'dine-in': {
          code: 'DineIn',
          display: 'Dine In'
        },
        'pick-up': {
          code: 'TakeOut',
          display: 'Pickup'
        },
        'room-service': {
          code: 'RoomService',
          display: 'Room Service'
        },
        shipping: {
          code: 'Shipping',
          display: 'Shipping'
        }
      };

      const attribute = attributeCodeMap[orderDetails.fulfillmentType] || {
        code: orderDetails.fulfillmentType,
        display: ''
      };
      const enabledAttribute = selectedLocation.attributes.find(a => a.value && a.code === attribute.code);

      if (!enabledAttribute) {
        dispatch('session/unsetLocation', null, { root: true });

        dispatch('session/updateOrderDetails', {
          fulfillmentType: null,
          menuType: null,
          orderType: 'standard'
        }, { root: true });

        dispatch('session/updateRouteParamsDirectly', {
          merchantLocationId: null,
          ml: null
        }, { root: true });

        dispatch('modals/openModal', {
          name: 'notice',
          data: {
            description: 'This order mode is currently unavailable at your selected location. Please select a valid location and order mode to place an order.',
            title: attribute.display ? `${attribute.display} Unavailable` : 'Unavailable Order Mode'
          }
        }, { root: true });
      }
    }
  },

  mutations: {
    SET_CURRENT_SEARCH_PAGE(state, page) {
      state.searchCurrentPage = page;
    },

    SET_SEARCH_FILTERS(state, filters) {
      state.searchFilters = filters;
    },

    SET_SEARCH_LOADING_STATUS(state, areLoading) {
      state.searchResultsAreLoading = areLoading;
    },

    SET_SEARCH_RESULTS(state, {
      data, paging = {
        totalPages: 1
      }
    }) {
      state.searchResults = data;
      state.searchTotalPages = paging.totalPages;
    },

    SET_SEARCH_QUERY(state, query) {
      state.searchQuery = query;
    },

    SET_SELECTED_LOCATION(state, location) {
      const updated = { ...state.selectedLocation, ...location };

      state.selectedLocation.address = updated.address;
      state.selectedLocation.allowAfterHoursOrdering = updated.allowAfterHoursOrdering;
      state.selectedLocation.allowSameDayOutsideBusinessHoursOrdering = updated.allowSameDayOutsideBusinessHoursOrdering;
      state.selectedLocation.attributes = updated.attributes || [];
      state.selectedLocation.businessLocationId = updated.businessLocationId;
      state.selectedLocation.businessLocationId = updated.businessLocationId;
      state.selectedLocation.gtmLocationId = updated.gtmLocationId;
      state.selectedLocation.dailyOperatingSchedules = updated.dailyOperatingSchedules;
      state.selectedLocation.description = updated.displayName || updated.description;
      state.selectedLocation.leadPickupTime = updated.leadPickupTime;
      state.selectedLocation.maximumOrderAheadDays = updated.maximumOrderAheadDays || 0;
      state.selectedLocation.menuId = updated.menuId;
      state.selectedLocation.outsideGeoFence = updated.outsideGeoFence;
      state.selectedLocation.outsideGeoFenceErrorModel = updated.outsideGeoFenceErrorModel;
      state.selectedLocation.outsideGeoFenceWarning = updated.outsideGeoFenceWarning;
      state.selectedLocation.outsideGeoFenceWarningErrorModel = updated.outsideGeoFenceWarningErrorModel;
      state.selectedLocation.paymentLocationId = updated.paymentLocationId;
      state.selectedLocation.phoneNumber = updated.phoneNumber;
      state.selectedLocation.pos = updated.pos;
      state.selectedLocation.storeId = updated.storeId;
      state.selectedLocation.timeZone = updated.timeZone;
      state.selectedLocation.validations = updated.validations || [];
      state.selectedLocation.tippingOptions = updated.tippingOptions || [];
    },

    SET_SELECTED_LOCATION_ASAP_PICKUP(state, timestamp) {
      state.selectedLocation.asapPickupTimestamp = timestamp;
    },

    SET_SELECTED_LOCATION_LOAD_STATUS(state, isLoading) {
      state.selectedLocation.isLoading = isLoading;
    },

    SET_SHOW_MORE_LOADING_STATUS(state, areLoading) {
      state.searchMoreResultsAreLoading = areLoading;
    },

    SET_PAYMENT_GATEWAY(state, info) {
      state.selectedLocation.paymentGateway = info;
    },

    UNSET_SELECTED_LOCATION(state) {
      state.selectedLocation.address = {};
      state.selectedLocation.allowAfterHoursOrdering = false;
      state.selectedLocation.allowSameDayOutsideBusinessHoursOrdering = false;
      state.selectedLocation.asapPickupTimestamp = null;
      state.selectedLocation.attributes = [];
      state.selectedLocation.businessLocationId = null;
      state.selectedLocation.businessLocationId = null;
      state.selectedLocation.dailyOperatingSchedules = null;
      state.selectedLocation.description = null;
      state.selectedLocation.isLoading = false;
      state.selectedLocation.leadPickupTime = null;
      state.selectedLocation.maximumOrderAheadDays = 0;
      state.selectedLocation.menuId = null;
      state.selectedLocation.outsideGeoFence = false;
      state.selectedLocation.outsideGeoFenceErrorModel = null;
      state.selectedLocation.outsideGeoFenceWarning = false;
      state.selectedLocation.outsideGeoFenceWarningErrorModel = null;
      state.selectedLocation.paymentGateway = {
        methods: {},
        providers: {}
      };
      state.selectedLocation.paymentLocationId = null;
      state.selectedLocation.phoneNumber = null;
      state.selectedLocation.pos = {};
      state.selectedLocation.storeId = null;
      state.selectedLocation.timeZone = null;
      state.selectedLocation.validations = [];
      state.selectedLocation.tippingOptions = [];
    }
  }
};
